import React, { createContext, useCallback, useContext, useMemo } from "react";

import { IamAuthContext } from "@megaron/auth-react";
import { SignalDoc } from "@megaron/docs-contracts";
import { useClientManager } from "@megaron/react-clients";
import { Uuid } from "@megaron/uuid";

type SignalsContextValue = {
  signals: SignalDoc[];
  unreadSignals: SignalDoc[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
  markRead: (docIds: SignalDoc["docId"][]) => void;
};

const SignalsContext = createContext<SignalsContextValue>({
  signals: [],
  unreadSignals: [],
  isLoading: false,
  isError: false,
  refetch: () => Promise.resolve(),
  markRead: () => undefined,
});

const signalsQueryKey = (userKey: string | undefined, limit: number) =>
  ["docs", "searchSignals", userKey, { limit }] as const;

export const SignalsProvider: React.FC<{
  children: React.ReactNode;
  isAuthLoaded: boolean;
}> = ({ children, isAuthLoaded }) => {
  const auth = useContext(IamAuthContext);
  const userKey = auth.iamUser?.id;
  const signalsLimit = 1000;

  const query = useClientManager("docs")
    .searchSignals()
    .useQuery(
      { limit: signalsLimit },
      {
        enabled: isAuthLoaded && Boolean(userKey),
        queryKey: [...signalsQueryKey(userKey, signalsLimit)],
        refetchInterval: isAuthLoaded && Boolean(userKey) ? 5_000 : false,
        refetchIntervalInBackground: false,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
        refetchOnReconnect: true,
      },
    );

  const setReadMutation = useClientManager("docs")
    .setSignalRead()
    .useMutation({
      onSuccess: () => {
        void query.refetch();
      },
    });

  const setThreadReadMutation = useClientManager("invoices").setIsRead().useMutation();

  const setTicketReadMutation = useClientManager("support").setTicketRead().useMutation();

  const signals = useMemo((): SignalDoc[] => query.data?.items ?? [], [query.data?.items]);

  const unreadSignals = useMemo(() => signals.filter((signal) => !signal.isRead), [signals]);

  const markDomainRead = useCallback(
    (docIds: SignalDoc["docId"][], allSignals: SignalDoc[]) => {
      const signalsByDocId = new Map(allSignals.map((signal) => [signal.docId, signal]));

      for (const docId of docIds) {
        const signal = signalsByDocId.get(docId);
        if (!signal) continue;

        if (signal.signalType === "unreadInvoice")
          setThreadReadMutation.mutate({ threadUuid: Uuid(signal.subjectDocId), isRead: true });

        if (signal.signalType === "unreadTicket")
          setTicketReadMutation.mutate({ id: Uuid(signal.subjectDocId), isRead: true });
      }
    },
    [setThreadReadMutation, setTicketReadMutation],
  );

  const markRead = useCallback(
    (docIds: SignalDoc["docId"][]) => {
      if (docIds.length === 0 || setReadMutation.isLoading) return;
      setReadMutation.mutate({ docIds, isRead: true });
      markDomainRead(docIds, signals);
    },
    [setReadMutation, markDomainRead, signals],
  );

  return (
    <SignalsContext.Provider
      value={{
        signals,
        unreadSignals,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch,
        markRead,
      }}
    >
      {children}
    </SignalsContext.Provider>
  );
};

export const useSignals = (): SignalsContextValue => useContext(SignalsContext);
